/**
 * Droits de l'utilisateur connecté, lus dans son jeton d'accès — pour l'AFFICHAGE seulement.
 *
 * Le jeton porte la liste de ses permissions (`permissions`, résolues à l'émission par l'API).
 * Le bureau s'en sert pour savoir quels boutons montrer : un administrateur voit « Nouvelle
 * gamme », un simple lecteur non. Ce n'est PAS une protection : c'est l'API qui refuse (HTTP 403)
 * une action sans le droit, et qui reste l'unique autorité. Un jeton peut aussi retarder de
 * quelques minutes sur un changement de rôle : au pire un bouton de trop, jamais un droit de trop.
 *
 * Fonctions pures : aucune dépendance au navigateur au-delà de `atob`.
 */

/** Permissions portées par un jeton ; ensemble vide si le jeton est absent ou illisible. */
export function permissionsDuJeton(jeton: string | null): ReadonlySet<string> {
  if (!jeton) return new Set();
  const morceaux = jeton.split('.');
  const charge = morceaux[1];
  if (morceaux.length !== 3 || !charge) return new Set();

  try {
    // Base64 « URL » : `-` et `_` à la place de `+` et `/`, sans le bourrage `=`.
    const base64 = charge.replace(/-/g, '+').replace(/_/g, '/');
    const rembourre = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const octets = Uint8Array.from(atob(rembourre), (c) => c.charCodeAt(0));
    const contenu = JSON.parse(new TextDecoder().decode(octets)) as unknown;
    if (typeof contenu !== 'object' || contenu === null) return new Set();
    const permissions = (contenu as { permissions?: unknown }).permissions;
    if (!Array.isArray(permissions)) return new Set();
    return new Set(permissions.filter((p): p is string => typeof p === 'string'));
  } catch {
    return new Set();
  }
}

export interface DroitsGammes {
  /** Créer, modifier, archiver une gamme ; composer un brouillon. */
  readonly peutGerer: boolean;
  /** Publier une version, ou archiver une version publiée. */
  readonly peutPublier: boolean;
  /** Voir les prix. */
  readonly peutVoirPrix: boolean;
  /**
   * Saisir des prix. Il faut aussi les VOIR : qui les écrirait sans les voir enverrait un contenu
   * sans prix et effacerait ceux qui existent.
   */
  readonly peutEcrirePrix: boolean;
  /** Lire les compteurs : sans ce droit, le prochain entretien ne se calcule pas (le serveur le dit). */
  readonly peutLireCompteurs: boolean;
  /** Créer, désigner, archiver un compteur (le paramétrage). */
  readonly peutGererCompteurs: boolean;
  /** Saisir ou rectifier un relevé de compteur. */
  readonly peutReleverCompteurs: boolean;
  /** Enregistrer, rectifier ou annuler un entretien réalisé (l'ancre du prochain entretien). */
  readonly peutEnregistrerEntretien: boolean;
}

export function droitsGammes(permissions: ReadonlySet<string>): DroitsGammes {
  const voirPrix = permissions.has('pricing:read:tenant');
  return {
    peutGerer: permissions.has('maintenance:manage:tenant'),
    peutPublier: permissions.has('maintenance:publish:tenant'),
    peutVoirPrix: voirPrix,
    peutEcrirePrix: voirPrix && permissions.has('pricing:manage:tenant'),
    peutLireCompteurs: permissions.has('meter:read:tenant'),
    peutGererCompteurs: permissions.has('meter:manage:tenant'),
    peutReleverCompteurs: permissions.has('meter:record:tenant'),
    peutEnregistrerEntretien: permissions.has('maintenance:record:tenant'),
  };
}

export interface DroitsBons {
  /** Lire les bons d'intervention (tous les rôles de bureau). */
  readonly peutLireBons: boolean;
  /** Saisir, modifier, soumettre, rouvrir et annuler un bon, et trier ses suites à donner. */
  readonly peutSaisirBons: boolean;
  /** Valider un bon (il écrit dans les compteurs et dans l'entretien réalisé : livraison I1.2). */
  readonly peutValiderBons: boolean;
  /** Créer, modifier, archiver les natures de bon et installer le jeu de départ. */
  readonly peutConfigurerNatures: boolean;
  /** Voir le chiffrage du bon (compris / à facturer) : la route exige le droit des prix ET celui de lire le planning. */
  readonly peutVoirChiffrage: boolean;
}

export function droitsBons(permissions: ReadonlySet<string>): DroitsBons {
  return {
    peutLireBons: permissions.has('intervention:read:tenant'),
    peutSaisirBons: permissions.has('intervention:manage:tenant'),
    peutValiderBons: permissions.has('intervention:review:tenant'),
    peutConfigurerNatures: permissions.has('intervention:configure:tenant'),
    peutVoirChiffrage: permissions.has('pricing:read:tenant') && permissions.has('planning:read:tenant'),
  };
}

/** Lot T4 : droits sur les machines du parc (valider, fusionner, corriger). */
export interface DroitsParc {
  readonly peutGererMachines: boolean;
}

export function droitsParc(permissions: ReadonlySet<string>): DroitsParc {
  return { peutGererMachines: permissions.has('asset:manage:tenant') };
}

export interface DroitsPlanning {
  /** Lire le planning, la liste « À affecter » et les techniciens (tous les rôles de bureau). */
  readonly peutLirePlanning: boolean;
  /** Créer, corriger, affecter, déplacer, faire, annuler et rouvrir une visite ; créer, corriger et archiver un technicien. */
  readonly peutGererPlanning: boolean;
  /** Créer et corriger le contrat d’entretien d’un site (2026-10-30). */
  readonly peutGererContrats: boolean;
}

export function droitsPlanning(permissions: ReadonlySet<string>): DroitsPlanning {
  return {
    peutLirePlanning: permissions.has('planning:read:tenant'),
    peutGererPlanning: permissions.has('planning:manage:tenant'),
    peutGererContrats: permissions.has('contract:manage:tenant'),
  };
}
